<script setup lang="ts">
import posthog from 'posthog-js'
import UpdatePasswordDialog from '@/pages/settings/dialogs/UpdatePasswordDialog.vue'
import { updateProfile, uploadEmployerProfilePicture } from '@swipe4work/api-orval'
import type { ImageDto, UpdateEmployerProfileCommandDto } from '@swipe4work/models-orval'
import UserProfileContent from './components/UserProfileContent.vue'
import { isContactInfoComplete } from '@/utils/contact-info-completeness'

definePage({
  name: '/settings/UserProfile',
  path: '/instellingen/gebruikersprofiel',
  meta: {
    layout: 'false',
  },
})

const { t } = useI18n()
const toast = useToast()
const dialog = useDynamicDialog()

const authStore = useAuthStore()
const profilePictureRef = ref<ImageDto | undefined>()

const { employerProfile, isFirebaseAuth } = storeToRefs(authStore)

watch(employerProfile, profile => {
  profilePictureRef.value = profile?.profilePicture
}, { immediate: true })

const uploadProfilePicture = async (_previousImage: ImageDto | undefined, file: File) => await uploadEmployerProfilePicture({ File: file })

async function openUpdatePasswordDialog() {
  dialog.open(UpdatePasswordDialog, {
    props: {
      dismissableMask: true,
      style: {
        width: 'fit-content',
        height: 'auto',
      },
    },
  })
}

async function onFormSubmit({ values, valid }: {
  values: UpdateEmployerProfileCommandDto | undefined
  valid: boolean
}) {
  if (!valid || !values) {
    toast.error(t('settings.userProfile.validation.formValidationErrors'))

    return
  }

  const wasContactInfoIncomplete = !isContactInfoComplete(employerProfile.value)

  updateProfile(values)
    .then(async () => {
      toast.success(t('settings.changesSaved'))
      await authStore.fetchEmployerProfile()

      if (wasContactInfoIncomplete && isContactInfoComplete(employerProfile.value)) {
        posthog.capture('contact-info-nudge:completed', { surface: 'settings' })
      }
    })
    .catch(error => {
      toast.error(t('settings.userProfile.validation.profileUpdateFailed'))
      throw error
    })
}
</script>

<template>
  <UserProfileContent
    v-model:profile-picture="profilePictureRef"
    :employer-profile="employerProfile"
    :is-firebase-auth="isFirebaseAuth"
    :upload-profile-picture="uploadProfilePicture"
    @submit-profile="onFormSubmit"
    @open-update-password="openUpdatePasswordDialog"
  />
</template>
